import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import i18next from "i18next";
import memoizeOne from "memoize-one";

import { CalendarView } from "api/generated/model";
import { RawMultiSelectInput } from "components/form/legacyFields/multiSelectInput";
import { SelectInputOption } from "components/form/legacyFields/selectInput";

interface RawCommonProps {
    values: Array<number | null>;
    calendars: Array<CalendarView>;
    name: string;
    className?: string;
}

type RawReadOnlyProps = RawCommonProps;

interface RawEditProps extends RawCommonProps {
    dropdownMatchSelectWidth?: boolean;
    onChange: (calendarId: number | null) => void;
}

type RawMultiCalendarSelectProps =
    | ({ type: "READ_ONLY" } & RawReadOnlyProps)
    | ({ type: "EDIT" } & RawEditProps);

const NULL_KEY = "NULL";

function calendarIdToKey(id: number | null): string {
    if (id === null) {
        return NULL_KEY;
    } else {
        return id.toString();
    }
}

function keyToCalendarId(key: string): number | null {
    if (key === NULL_KEY) {
        return null;
    } else {
        return parseInt(key, 10);
    }
}

const buildOptions = memoizeOne(
    (
        calendars: Array<CalendarView>,
        _lang: string
    ): Array<SelectInputOption> => {
        const options: Array<SelectInputOption> = [
            {
                value: NULL_KEY,
                label: t("ReviewerOffset_Days"),
            },
        ];
        calendars.forEach((calendar) => {
            options.push({
                value: calendarIdToKey(calendar.id),
                label: calendar.name,
            });
        });
        return options;
    }
);

class RawMultiCalendarSelect extends React.PureComponent<RawMultiCalendarSelectProps> {
    render() {
        const { values: calendarIds, calendars, name, className } = this.props;
        const options = buildOptions(calendars, i18next.language);
        const keys: Array<string> = calendarIds.map(calendarIdToKey);

        switch (this.props.type) {
            case "READ_ONLY": {
                return (
                    <RawMultiSelectInput
                        type="READ_ONLY"
                        name={name}
                        options={options}
                        values={keys}
                        display="Input"
                        className={className}
                    />
                );
            }
            case "EDIT": {
                return (
                    <RawMultiSelectInput
                        type="EDIT"
                        name={name}
                        options={options}
                        values={keys}
                        className={className}
                        dropdownMatchSelectWidth={
                            this.props.dropdownMatchSelectWidth
                        }
                        onChange={this.handleChange}
                    />
                );
            }
            default: {
                return assertNever(this.props);
            }
        }
    }

    private handleChange = (key: string | undefined) => {
        switch (this.props.type) {
            case "EDIT": {
                const { onChange } = this.props;
                if (key !== undefined) {
                    const newCalendarId: number | null = keyToCalendarId(key);
                    onChange(newCalendarId);
                } else {
                    throw new Error(
                        "RawMultiCalendarSelect expects a value passed to onChange to be always present"
                    );
                }
                break;
            }
            case "READ_ONLY": {
                throw new Error("READ_ONLY has no onChange");
            }
            default: {
                assertNever(this.props);
            }
        }
    };
}

export type { RawMultiCalendarSelectProps };
export { RawMultiCalendarSelect };
